/**
 * Create a policy document, or change what describes it.
 *
 * The key is fixed once created: it is the document's address — the public URL
 * `/legal/<key>` and the name a feature checks consent by — and changing it
 * would silently break both. The text is never edited here; it lives in
 * versions, which is what makes every acceptance traceable to exact words.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  Alert,
  Button,
  Input,
  Modal,
  Select,
  Switch,
  Textarea,
  api,
  toast,
} from '@connect/web-shared';

import { legalKeys, type DocumentDetail, type DocumentRow, type LegalOptions } from './types';

const KEY = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;

/** `Privacy Policy` → `privacy-policy`, as a starting point the author can change. */
export function keyFrom(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

export function DocumentFormDialog({
  open,
  onClose,
  document,
  options,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** `null` creates one. */
  document: DocumentRow | null;
  options: LegalOptions | undefined;
  onSaved?: (saved: DocumentDetail) => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [kind, setKind] = useState('');
  const [description, setDescription] = useState('');
  const [service, setService] = useState('');
  const [channel, setChannel] = useState('');
  const [isPublic, setIsPublic] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(document?.title ?? '');
    setKey(document?.key ?? '');
    setKeyTouched(document !== null);
    setKind(document?.kind ?? options?.document_kinds[0]?.value ?? '');
    setDescription(document?.description ?? '');
    setService(document?.service ?? '');
    setChannel(document?.channel ?? '');
    setIsPublic(document?.is_public ?? false);
  }, [open, document, options]);

  const save = useMutation({
    mutationFn: () => {
      const fields = {
        title,
        description: description || null,
        service: service || null,
        channel: channel || null,
        is_public: isPublic,
      };
      return document
        ? api.patch<DocumentDetail>(`/legal/documents/${document.public_id}`, fields)
        : api.post<DocumentDetail>('/legal/documents', { ...fields, key, kind });
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: legalKeys.all });
      toast.success(
        document ? 'Document updated' : 'Document created',
        document ? undefined : 'Write its first version, then publish it.',
      );
      onSaved?.(saved);
      onClose();
    },
  });

  const keyValid = document !== null || KEY.test(key);
  const valid = title.trim().length >= 2 && keyValid && kind !== '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdrop={false}
      title={document ? 'Edit document' : 'New document'}
      description={
        document
          ? 'Its key and kind are fixed. The text is changed by writing a new version.'
          : 'A document holds versions of one text: terms, a privacy notice, a channel policy.'
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!valid}>
            {document ? 'Save' : 'Create document'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {save.error instanceof ApiError ? (
          <Alert tone="danger">{save.error.message}</Alert>
        ) : null}
        <Input
          label="Title"
          required
          value={title}
          maxLength={160}
          onChange={(event) => {
            setTitle(event.target.value);
            if (!keyTouched) setKey(keyFrom(event.target.value));
          }}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Key"
            required
            value={key}
            disabled={document !== null}
            maxLength={60}
            hint="Its address: lowercase letters, digits and hyphens."
            error={key && !keyValid ? 'Lowercase letters, digits and hyphens only.' : undefined}
            onChange={(event) => {
              setKeyTouched(true);
              setKey(event.target.value.toLowerCase());
            }}
          />
          <Select
            label="Kind"
            required
            value={kind}
            disabled={document !== null}
            options={options?.document_kinds ?? []}
            onChange={(event) => setKind(event.target.value)}
          />
        </div>
        <Textarea
          label="Description"
          rows={2}
          maxLength={2000}
          hint="For staff: what this document is for. Clients do not see it."
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Service"
            placeholder="Every service"
            value={service}
            options={options?.services ?? []}
            onChange={(event) => setService(event.target.value)}
          />
          <Select
            label="Channel"
            placeholder="Not channel-specific"
            value={channel}
            options={options?.channels ?? []}
            onChange={(event) => setChannel(event.target.value)}
          />
        </div>
        <Switch
          label="Public"
          hint={
            key
              ? `Anyone can read the version in effect at /legal/${key}, signed in or not — for a privacy policy a store listing links to.`
              : 'Anyone can read the version in effect, signed in or not.'
          }
          checked={isPublic}
          onChange={(event) => setIsPublic(event.target.checked)}
        />
      </div>
    </Modal>
  );
}
